import { computed, observable, action, makeObservable } from 'mobx'
import { createTransformer } from 'mobx-utils'
import Team from 'iam/models/Team'
import { TeamItemViewModel } from './TeamItem/TeamItemViewModel'
import Workspace from 'iam/models/Workspace'
import orderBy from 'lodash/orderBy'

export class TeamListViewModel {
  @observable searchText = ''
  // true means desc, false means asc
  @observable sortOrder = true
  @observable sortField: keyof Team = 'name'

  constructor(
    private workspace: Workspace,
    private getTeams: () => Team[],
    private getCurrentTeam: () => Team,
  ) {
    makeObservable(this)
  }

  /**
   * Use to create a Team Item View Model for each Team instance
   */
  createTeamItemViewModel = createTransformer<Team, TeamItemViewModel>(
    (team) => {
      const vm = new TeamItemViewModel(team, this.workspace)
      vm.activate()
      return vm
    },
    (vm) => vm && vm.deactivate(),
  )

  /**
   * TeamItemViewModels where the current user is a member
   */
  @computed
  get teams() {
    return this.getTeams()
  }

  @computed
  get teamItems() {
    return this.filtered.map(this.createTeamItemViewModel)
  }

  @computed
  get currentTeam() {
    return this.getCurrentTeam()
  }

  @computed
  get filtered() {
    if (this.searchText === '') {
      return orderBy(this.teams, [this.sortField], this.sortOrder)
    }

    return orderBy(
      this.teams.filter(
        (team) =>
          team.name.toLowerCase().includes(this.searchText.toLowerCase()) ||
          team.owner.member.displayName
            .toLowerCase()
            .includes(this.searchText.toLowerCase()),
      ),
      [this.sortField],
      this.sortOrder,
    )
  }

  @action.bound
  sort(field: keyof Team) {
    if (this.sortField === field) {
      this.sortOrder = !this.sortOrder
    } else {
      this.sortField = field
      this.sortOrder = true
    }
  }

  @action.bound
  setSearchText(val: string) {
    this.searchText = val
  }
}
